import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './index.scss';
import Button from '../../Button';
import ProgressBar from '../../ProgressBar';

// 自定义数字输入框组件
function CustomNumberInput({ value, onChange, placeholder, className, min, max, step }) {
  const inputRef = useRef(null);

  const handleIncrement = () => {
    const currentValue = parseFloat(value) || 0;
    const newValue = currentValue + (parseFloat(step) || 1);
    if (max === undefined || newValue <= max) {
      onChange(newValue.toString());
    }
  };

  const handleDecrement = () => {
    const currentValue = parseFloat(value) || 0;
    const newValue = currentValue - (parseFloat(step) || 1);
    if (min === undefined || newValue >= min) {
      onChange(newValue.toString());
    }
  };

  const handleArrowClick = (e, isUp) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUp) {
      handleIncrement();
    } else {
      handleDecrement();
    }
  };

  return (
    <div className={`custom-number-input ${className}`}>
      <input
        ref={inputRef}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        className="unitconverter_input"
      />
      <div className="custom-number-arrows">
        <div 
          className="custom-number-arrow custom-number-arrow-up"
          onClick={(e) => handleArrowClick(e, true)}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 2L10 8H2L6 2Z" fill="#00ff00" stroke="#00cc66" strokeWidth="0.5"/>
          </svg>
        </div>
        <div 
          className="custom-number-arrow custom-number-arrow-down"
          onClick={(e) => handleArrowClick(e, false)}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 10L2 4H10L6 10Z" fill="#00ff00" stroke="#00cc66" strokeWidth="0.5"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

// 自定义下拉菜单组件
function CustomSelect({ value, onChange, options, placeholder, className }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      if (isOpen && headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        const isMobile = window.innerWidth <= 768;
        
        if (isMobile) {
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: 0,
            width: window.innerWidth
          });
        } else {
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width
          });
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        // 手机模式下，让下拉菜单占满屏幕宽度
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: 0,
          width: window.innerWidth
        });
      } else {
        // 桌面模式下，保持原有逻辑
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (optionValue) => {
    setSelectedValue(optionValue);
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(option => option.value === selectedValue);

  return (
    <div className={`custom-select ${className}`}>
      <div 
        ref={headerRef}
        className={`custom-select-header ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
      >
        <span className="custom-select-value">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="custom-select-arrow">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00ff00" strokeWidth="2">
            <polyline points="6,9 12,15 18,9"></polyline>
          </svg>
        </div>
      </div>
      
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="custom-select-dropdown"
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 999999
          }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`custom-select-option ${option.value === selectedValue ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default function UnitConverter() {
  const [converterType, setConverterType] = useState('weight');
  const [inputValue, setInputValue] = useState('');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [result, setResult] = useState('');
  const [history, setHistory] = useState([]);

  // 重量单位转换系数（以千克为基准）
  const weightConversions = {
    kg: 1,
    g: 0.001,
    mg: 0.000001,
    t: 1000,
    lb: 0.45359237,
    oz: 0.028349523125,
    st: 6.35029318,
    cwt: 50.80234544,
    'long ton': 1016.0469088,
    'short ton': 907.18474,
    ct: 0.0002,
    gr: 0.00006479891
  };

  // 长度单位转换系数（以米为基准）
  const lengthConversions = {
    m: 1,
    cm: 0.01,
    mm: 0.001,
    km: 1000,
    μm: 0.000001,
    nm: 0.000000001,
    pm: 0.000000000001,
    in: 0.0254,
    ft: 0.3048,
    yd: 0.9144,
    mile: 1609.344,
    nmi: 1852
  };

  // 单位显示名称
  const unitNames = {
    weight: {
      kg: '千克 (kg)',
      g: '克 (g)',
      mg: '毫克 (mg)',
      t: '吨 (t)',
      lb: '磅 (lb)',
      oz: '盎司 (oz)',
      st: '英石 (st)',
      cwt: '英担 (cwt)',
      'long ton': '英吨 (long ton)',
      'short ton': '美吨 (short ton)',
      ct: '克拉 (ct)',
      gr: '格令 (gr)'
    },
    length: {
      m: '米 (m)',
      cm: '厘米 (cm)',
      mm: '毫米 (mm)',
      km: '千米 (km)',
      μm: '微米 (μm)',
      nm: '纳米 (nm)',
      pm: '皮米 (pm)',
      in: '英寸 (in)',
      ft: '英尺 (ft)',
      yd: '码 (yd)',
      mile: '英里 (mile)',
      nmi: '海里 (nmi)'
    }
  };

  // 获取当前类型的单位列表
  const getUnits = () => {
    return converterType === 'weight' ? weightConversions : lengthConversions;
  };

  // 获取选项列表
  const getOptions = () => {
    const units = getUnits();
    return Object.keys(units).map(unit => ({
      value: unit,
      label: unitNames[converterType][unit]
    }));
  };

  // 转换函数
  const convert = () => {
    if (!inputValue || !fromUnit || !toUnit) return;

    const units = getUnits();
    const baseValue = parseFloat(inputValue) * units[fromUnit];
    const convertedValue = baseValue / units[toUnit];
    
    setResult(convertedValue.toFixed(6));

    // 添加到历史记录
    const newRecord = {
      id: Date.now(),
      type: converterType,
      input: `${inputValue} ${unitNames[converterType][fromUnit]}`,
      output: `${convertedValue.toFixed(6)} ${unitNames[converterType][toUnit]}`,
      timestamp: new Date().toLocaleTimeString()
    };
    setHistory(prev => [newRecord, ...prev.slice(0, 9)]); // 保留最近10条记录
  };

  // 当输入值或单位改变时自动转换
  useEffect(() => {
    if (inputValue && fromUnit && toUnit) {
      convert();
    }
  }, [inputValue, fromUnit, toUnit, converterType]);

  // 交换单位
  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
  };

  // 清空历史记录
  const clearHistory = () => {
    setHistory([]);
  };

  return (
    <div className="unitconverter_container">
      {/* 转换类型选择 */}
      <div className="unitconverter_type-selection">
        <Button
          onClick={() => setConverterType('weight')}
          className={converterType === 'weight' ? 'active' : ''}
        >
          重量换算
        </Button>
        <Button
          onClick={() => setConverterType('length')}
          className={converterType === 'length' ? 'active' : ''}
        >
          长度换算
        </Button>
      </div>

      {/* 转换区域 */}
      <div className="unitconverter_conversion-area">
        {/* 输入区域 */}
        <div className="unitconverter_input-area">
          <CustomNumberInput
            value={inputValue}
            onChange={setInputValue}
            placeholder="输入数值"
            min="0"
            step="any"
          />
          <CustomSelect
            value={fromUnit}
            onChange={setFromUnit}
            options={getOptions()}
            placeholder="选择单位"
            className="unitconverter_select"
          />
        </div>

        {/* 转换按钮 */}
        <div className="unitconverter_swap-button-container">
          <Button
            onClick={swapUnits}
            className="unitconverter_swap-button"
          >
            ⇄ 交换
          </Button>
        </div>

        {/* 输出区域 */}
        <div className="unitconverter_output-area">
          <div className="unitconverter_result">
            {result ? `${result}` : '转换结果将在这里显示'}
          </div>
          <CustomSelect
            value={toUnit}
            onChange={setToUnit}
            options={getOptions()}
            placeholder="选择单位"
            className="unitconverter_select"
          />
        </div>
      </div>

      {/* 历史记录 */}
      <div className="unitconverter_history-container">
        <div className="unitconverter_history-header">
          <span>转换历史 ({history.length})</span>
          {history.length > 0 && (
            <Button
              onClick={clearHistory}
              className="unitconverter_clear-history-button"
            >
              清空
            </Button>
          )}
        </div>
        <div className="unitconverter_scroll">
          {history.length === 0 ? (
            <div className="unitconverter_no-history-message">
              暂无转换记录
            </div>
          ) : (
            history.map((record, index) => (
              <div
                key={record.id}
                className={`unitconverter_history-record ${index % 2 === 0 ? 'even' : 'odd'}`}
              >
                <div className="unitconverter_history-record-header">
                  <span className="unitconverter_history-record-input">
                    {record.input} → {record.output}
                  </span>
                  <span className="unitconverter_history-record-timestamp">
                    {record.timestamp}
                  </span>
                </div>
                <div className="unitconverter_history-record-type">
                  {record.type === 'weight' ? '重量换算' : '长度换算'}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
} 